import React, { useContext, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart, setCart } from "../redux/action";
import { motion } from "framer-motion";
import { Footer, Navbar } from "../components";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/base_url";
import toast from "react-hot-toast";
import ReviewBox from "../components/ReviewBox";
import StockNotifyModal from "../components/StockNotifyModal";
import BulkInquiryModal from "../components/BulkInquiryModal";
import { FaWhatsapp } from "react-icons/fa";
import "../styles/index.css";
import '../styles/Product.css';
import { useCartPricing } from "../hooks/useCartPricing";


const Product = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [stocks, setStocks] = useState({});
  const [variants, setVariants] = useState([]);
  const [showDescModal, setShowDescModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  // ── Notify modal state ──────────────────────────────────────
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [notifyForm, setNotifyForm] = useState({
    user_id: "",
    product_id: "",
    email: "",
    quantity: 1,
  });

  // Out-of-stock variant that the user hovered / the page auto-detects
  const [oosVariant, setOosVariant] = useState(null);

  const location = useLocation();

  const reviewRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const state = useSelector((state) => state.handleCart);
  const dispatch = useDispatch();

  const { pricingMap } = useCartPricing(state);

  // ── Helpers ─────────────────────────────────────────────────

  const isVariantOutOfStock = (v) => {
    const stockVal = stocks[v.id];
    if (stockVal !== undefined) return stockVal === 0;
    return !v.is_available || v.stock_quantity === 0;
  };

  const openNotifyForVariant = (v) => {
    setNotifyProduct(v);
    setNotifyForm({ user_id: user?.id ?? "", product_id: v.id, email: user?.email ?? "", quantity: 1 });
    setNotifyOpen(true);
  };

  const p = product ? pricingMap?.[product.id] : null;
  const hasWholesale = p && p.discount_percent > 0;

  // When the current product itself is OOS, surface the first available variant banner
  useEffect(() => {
    if (!product) return;
    const currentOos =
      !product.is_available || Number(stocks[product.id] ?? product.stock_quantity) === 0;

    if (currentOos && variants.length > 0) {
      // Find the OOS variant matching the current product (same id) for the notify banner
      const match = variants.find((v) => Number(v.id) === Number(id));
      setOosVariant(match ?? null);
    } else {
      setOosVariant(null);
    }
  }, [product, variants, stocks, id]);

  // const handleShare = async () => {
  //   const url = window.location.href;
  //   if (navigator.share) {
  //     try {
  //       await navigator.share({ title: product?.product_name, text: "Check this product", url });
  //     } catch { }
  //   } else {
  //     try {
  //       await navigator.clipboard.writeText(url);
  //       toast.success("Link copied!");
  //     } catch {
  //       toast.error("Copy failed");
  //     }
  //   }
  // };

  const getCartQty = (productId) => {
    const item = state.find((p) => p.id === productId);
    return item ? item.qty : 0;
  };

  const goToReviews = () => {
    const position = reviewRef.current?.offsetTop - 80;
    window.scrollTo({ top: position, behavior: "smooth" });
  };

  const initiateCheckout = async () => {
    const token = sessionStorage.getItem("access");
    if (!token) {
      sessionStorage.setItem(
        "redirect_toast",
        "Please login first to continue checkout"
      );

      sessionStorage.setItem(
        "login_redirect",
        JSON.stringify({
          pathname: location.pathname,
          search: location.search,
          state: location.state,
        })
      );

      return navigate("/login");
    }
    const cartPayload = state.map((item) => ({ product_id: item.id, qty: item.qty }));
    try {
      const res = await api.post("checkout-initiate/", { user_id: user.id, cart: cartPayload });
      if (!res.data.status) { toast.error(res.data.message || "Checkout failed"); return; }
      if (res.data.updated_items?.length) toast.error("Some quantities were reduced due to low stock");
      if (res.data.removed_items?.length) toast.error("Some items removed because they are out of stock");
      const final = res.data.reserved_items.map((i) => ({
        id: i.product_id, qty: i.qty, product_name: i.product_name,
        price: Number(i.price) || 0, offer_price: i.offer_price ? Number(i.offer_price) : null,
        product_image: i.product_image, variant_label: i.variant_label ?? null,
      }));
      dispatch(setCart(final));
      navigate("/checkout");
    } catch (err) {
      console.error("initiateCheckout error:", err);
      toast.error("Unable to start checkout");
    }
  };

  const addItem = (product) => {
    const availableStock = stocks[product.id] ?? product.stock_quantity;
    if (availableStock && getCartQty(product.id) >= availableStock) {
      setStockError((prev) => ({ ...prev, [product.id]: true }));
      setDisabledQty((prev) => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setStockError((prev) => ({ ...prev, [product.id]: false }));
        setDisabledQty((prev) => ({ ...prev, [product.id]: false }));
      }, 2000);
      return;
    }
    const productWithVariant = {
      ...product,
      variant_label: product.quantity_unit
        ? (product.quantity_unit.toLowerCase() === "size"
          ? product.quantity
          : `${parseFloat(product.quantity)} ${product.quantity_unit}`)
        : null,
    };
    dispatch(addCart(productWithVariant));
  };

  const removeItem = (product) => dispatch(delCart(product));

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setLoadingSimilar(true);
      try {
        const res = await api.get(`/productlist/${id}/`);
        const data = res.data?.data;
        setProduct(data);

        if (data && !data.parent) {
          try {
            const resVar = await api.get(`/productvariantfilter/?parent_id=${data.id}`);
            const sortedVariants = (resVar.data?.data || []).sort(
              (a, b) => parseFloat(a.price) - parseFloat(b.price)
            );
            const formattedVariants = sortedVariants.map((v) => ({
              ...v,
              quantity:
                v.quantity_unit?.toLowerCase() === "size" || v.quantity_unit?.toLowerCase() === "color"
                  ? v.quantity
                  : String(parseFloat(v.quantity)),
              quantity_unit:
                v.quantity_unit?.toLowerCase() === "piece" ? "PC" : v.quantity_unit,
            }));
            setVariants(formattedVariants);
          } catch (err) { console.error("Variant error", err); }
        }

        if (user?.id && data?.id) {
          try {
            const favRes = await api.get(`/favorites/ids/?auth_id=${user.id}&product_id=${data.id}`);
            setIsFavorite(favRes.data?.is_favorite === true);
          } catch { }
        }

        if (data?.category) {
          try {
            const resSimilar = await api.get(`/productfilter/${data.category}/?page=1`);
            const list = resSimilar.data?.data || [];
            setSimilarProducts(list.filter((item) => item.id !== data.id));
          } catch (err) {
            console.error("Similar products error", err);
            setSimilarProducts([]);
          }
        } else {
          setSimilarProducts([]);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
        setLoadingSimilar(false);
      }
    };
    if (id) fetchProduct();
  }, [id, user?.id]);

  const toggleFavorite = async () => {
    if (!user) { toast.error("Please login first!"); navigate("/login"); return; }
    try {
      const res = await api.post(`/favorites/toggle/`, { product_id: product.id, auth_id: user.id });
      setIsFavorite(res.data?.is_favorite);
      toast.success(res.data?.is_favorite ? "Added to favorites ❤️" : "Removed from favorites");
    } catch { toast.error("Failed, please try again"); }
  };

  useEffect(() => {
    const getStock = async () => {
      try {
        const stockData = {};
        if (product?.id) {
          const resMain = await api.get(`/stock/?product_id=${product.id}`);
          stockData[product.id] = Number(resMain.data?.stock ?? product.stock_quantity ?? 0);
        }
        const similarIds = similarProducts.map((p) => p.id);
        if (similarIds.length > 0) {
          const results = await Promise.all(
            similarIds.map((pid) =>
              api.get(`/stock/?product_id=${pid}`)
                .then((r) => ({ id: pid, stock: Number(r.data?.stock ?? 0) }))
                .catch(() => ({ id: pid, stock: Number(similarProducts.find((sp) => sp.id === pid)?.stock_quantity ?? 0) }))
            )
          );
          results.forEach((r) => { stockData[r.id] = r.stock; });
        }
        setStocks((prev) => ({ ...prev, ...stockData }));
      } catch (error) {
        console.error("Stock fetch error:", error);
      }
    };
    if (product || similarProducts?.length > 0) getStock();
  }, [product, similarProducts]);

  // ── Sub-components ───────────────────────────────────────────

  const LoadingProduct = () => (
    <div className="container my-5 py-2">
      <div className="row">
        <div className="col-md-6 py-3"><Skeleton height={350} width={350} /></div>
        <div className="col-md-6 py-5">
          <Skeleton height={25} width={200} />
          <Skeleton height={80} />
          <Skeleton height={35} width={70} />
          <Skeleton height={40} width={110} />
          <Skeleton height={100} />
          <Skeleton height={35} width={110} inline={true} />
          <Skeleton className="mx-3" height={35} width={110} />
        </div>
      </div>
    </div>
  );

  const ShowProduct = () => {
    if (!product) return null;

    const hasOffer =
      product.offer_price !== null &&
      product.offer_price > 0 &&
      product.offer_price !== product.price;

    const available = Number(stocks[product.id] ?? product.stock_quantity ?? 0);
    const outOfStock = !product.is_available || available === 0;
    const stockLow = available <= 10 && available > 0;

    const variantLabel = (v) => {
      const unit = v.quantity_unit?.toLowerCase();
      return unit === "size" || unit === "color"
        ? v.quantity
        : `${v.quantity} ${v.quantity_unit}`;
    };

    return (
      <div className="container mt-3 mb-2 py-2">
        <div className="row align-items-center">

          {/* ── Image column ── */}
          <div className="col-md-6 col-sm-12 py-3 text-center">
            <div className="product-image-wrapper">
              {user && (
                <motion.div
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(); }}
                  initial={{ scale: 1 }}
                  animate={{ scale: isFavorite ? 1.15 : 1 }}
                  transition={{ duration: 0.25 }}
                  whileTap={{ scale: 0.9 }}
                  className="fav-btn-product"
                >
                  <motion.div className="fav-inner">
                    {isFavorite ? (
                      <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.3 }} className="heart-active" >❤️</motion.span>
                    ) : (
                      <span className="heart-inactive" >🤍</span>
                    )}
                  </motion.div>
                </motion.div>
              )}

              {stockError[product.id] && (
                <motion.div
                  initial={{ x: 80, opacity: 0 }}
                  animate={{ x: [80, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="product-stock-error-badge"
                >
                  Stock limit reached!
                </motion.div>
              )}

              <img
                className="img-fluid product-main-image"
                src={product.product_image}
                alt={product.product_name}
              />
              {outOfStock && <span className="stock-badge out-stock">Out of Stock</span>}
              {hasWholesale && (
                <span className="wholesale-badge">
                  Wholesale price applied · {p.discount_percent}% off
                </span>
              )}
              {!outOfStock && stockLow && (
                <span className="stock-badge low-stock">Hurry! Only {available} left</span>
              )}
              {hasOffer && (
                <span className="offer-badge">{Math.round(product.offer_percentage)}% OFF</span>
              )}
            </div>
          </div>

          {/* ── Info column ── */}
          <div className="col-md-6 py-3">
            <h4 className="product-category-label">{product.category_name}</h4>
            <h1 className="product-name">{product.product_name}</h1>

            <div className="rating-row">
              Rating: {product.average_rating}{" "}
              <i className="fa fa-star text-warning"></i>
              <button
                className="rating-link"
                onClick={goToReviews}
              >
                View Reviews
              </button>
            </div>

            <h4>
              {hasWholesale ? (
                <>
                  ₹{Number(p.wholesale_price).toLocaleString()}{" "}
                  <small className="text-danger text-decoration-line-through">
                    ₹{parseFloat(p.original_price).toLocaleString()}
                  </small>
                </>
              ) : hasOffer ? (
                <>
                  ₹{parseFloat(product.offer_price).toLocaleString()}{" "}
                  <small className="text-danger text-decoration-line-through">
                    ₹{parseFloat(product.price).toLocaleString()}
                  </small>
                </>
              ) : (
                `₹${parseFloat(product.price).toLocaleString()}`
              )}
            </h4>
        
            {/* ── Variant selector ── */}
            {variants.length > 0 && (
              <div className="variant-section mt-4">
                <h6 className="fw-bold mb-3">Available :</h6>
                <div className="d-flex flex-wrap gap-2">
                  {variants.map((v) => {
                    const vOos = isVariantOutOfStock(v);
                    const isActive = Number(id) === Number(v.id);

                    return (
                      <div key={v.id} className="variant-wrapper">
                        <button
                          onClick={() => {
                            if (vOos) {
                              // Show notify banner for this OOS variant
                              setOosVariant(v);
                            } else {
                              navigate(`/product/${v.id}`);
                            }
                          }}
                          className={`variant-btn ${isActive ? "active" : ""} ${vOos ? "variant-btn-oos" : ""}`}
                          title={vOos ? "Out of stock — click to get notified" : variantLabel(v)}
                          aria-disabled={vOos}
                        >
                          <span className={vOos ? "variant-label-strike" : ""}>
                            {variantLabel(v)}
                          </span>
                          {vOos && <span className="variant-oos-tag">Out of stock</span>}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* ── OOS Notify banner ── */}
                {oosVariant && (
                  <motion.div
                    key={oosVariant.id}
                    className="oos-notify-banner"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="oos-notify-icon">🔔</div>
                    <div className="oos-notify-text">
                      <strong>{variantLabel(oosVariant)}</strong> is currently out of stock
                      <span>Notify me when it's back in stock</span>
                    </div>
                    <button
                      className="oos-notify-cta"
                      onClick={() => openNotifyForVariant(oosVariant)}
                    >
                      Notify me
                    </button>
                    <button
                      className="oos-notify-close"
                      onClick={() => setOosVariant(null)}
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </motion.div>
                )}
              </div>
            )}

            {/* ── Action buttons ── */}
            <div className="product-btns-product mt-3" onClick={(e) => e.stopPropagation()}>
                {outOfStock ? (
                  <div className="d-flex flex-column gap-2">
                    <span className="out-stock-label">Out of Stock</span>
                    <button
                      className="btn-notify-main"
                      onClick={() => openNotifyForVariant(product)}
                    >
                      🔔 Notify me when available
                    </button>
                  </div>
                ) : getCartQty(product.id) === 0 ? (
                  <button className="btn-green" onClick={() => addItem(product)}>
                    Add to Cart
                  </button>
                ) : (
                  <div className="qty-box">
                    <button className="qty-btn" onClick={() => removeItem(product)}>
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="qty-value">{getCartQty(product.id)}</span>
                    <button className="qty-btn" onClick={() => addItem(product)} disabled={disabledQty[product.id]}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}

                {!outOfStock && (
                  <button className="btn-checkout mb-2" onClick={initiateCheckout} disabled={getCartQty(product.id) === 0}>
                    Go to checkout
                  </button>
                )}

                <button
                  type="button"
                  className="btn-wholesale-whatsapp mb-2"
                  onClick={() => setBulkModalOpen(true)}
                  title="Bulk / Wholesale Inquiry on WhatsApp"
                >
                  <FaWhatsapp size={17} /> Bulk Inquiry
                </button>
            </div>

            {/* Description */}
            <div className="mt-3">
              <p className="product-desc">
                {showDescModal
                  ? product.product_description
                  : `${product.product_description?.slice(0, 250)}...`}
              </p>

              {product.product_description?.length > 250 && (
                <button
                  type="button"
                  onClick={() => setShowDescModal(true)}
                  className="read-more-link"
                >
                  Read more
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const LoadingSimilar = () => (
    <div className="scroll-container my-4 py-3">
      {[...Array(5)].map((_, idx) => (
        <div key={idx} className="similar-card">
          <Skeleton height={130} className="skeleton-card" />
          <Skeleton height={16} width={100} className="skeleton-title" />
          <Skeleton height={14} width={70} className="skeleton-price" />
          <Skeleton height={32} width={100} className="skeleton-button" />
        </div>
      ))}
    </div>
  );

  const ShowSimilarProducts = () => (
    <div className="my-5 container">
      <h3 className="similar-section-title">You may also like</h3>
      <div className="scroll-container">
        {similarProducts.map((item) => {
          const hasOffer = item.offer_price !== null && Number(item.offer_price) > 0 && item.offer_price !== item.price;
          const available = Number(stocks[item.id] ?? item.stock_quantity ?? 0);
          const outOfStock = !item.is_available || available === 0;
          const stockLow = available <= 10 && available > 0;
          const p = pricingMap[item.id];
          const hasWholesale = p && p.discount_percent > 0;

          return (
            <div
              key={item.id}
              className={`similar-card ${outOfStock
                ? "similar-card-disabled"
                : ""
                }`}
              onClick={() => { if (!outOfStock) navigate(`/product/${item.id}`); }}
            >
              {hasOffer && <span className="sim-offer-badge">{Math.round(item.offer_percentage)}% OFF</span>}
              {outOfStock && <span className="sim-stock-badge out-stock">Out of Stock</span>}
              {!outOfStock && stockLow && <span className="sim-stock-badge low-stock">Only {available} left</span>}
              {stockError[item.id] && (
                <motion.div
                  initial={{ x: 80, opacity: 0 }}
                  animate={{ x: [80, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="product-stock-error-badge"
                >
                  Stock limit reached!
                </motion.div>
              )}

              <div className="sim-img-wrapper">
                <img src={item.product_image} alt={item.product_name} />
              </div>
              <div className="sim-content">
                <h6 className="sim-title">
                  {item.product_name.length > 15 ? item.product_name.substring(0, 15) + "..." : item.product_name}
                </h6>
                <div className="sim-price-wrap">
                  {hasWholesale ? (
                    <>
                      <span className="sim-price-offer">₹{Number(p.wholesale_price).toLocaleString()}</span>
                      <span className="sim-price-original"><s>₹{parseFloat(p.original_price).toLocaleString()}</s></span>
                    </>
                  ) : hasOffer ? (
                    <>
                      <span className="sim-price-offer">₹{parseFloat(item.offer_price).toLocaleString()}</span>
                      <span className="sim-price-original"><s>₹{parseFloat(item.price).toLocaleString()}</s></span>
                    </>
                  ) : (
                    <span className="sim-price">₹{parseFloat(item.price).toLocaleString()}</span>
                  )}
                </div>
              </div>
              <div className="sim-btns" onClick={(e) => e.stopPropagation()}>
                {item.stock_quantity === 0 ? (
                  <span className="out-stock-label">Out of Stock</span>
                ) : getCartQty(item.id) === 0 ? (
                  <button className="btn-buy" onClick={() => addItem(item)}>Add to Cart</button>
                ) : (
                  <div className="qty-box">
                    <button className="qty-btn" onClick={() => removeItem(item)}><i className="fas fa-minus"></i></button>
                    <span className="qty-value">{getCartQty(item.id)}</span>
                    <button className="qty-btn" onClick={() => addItem(item)} disabled={disabledQty[item.id]}><i className="fas fa-plus"></i></button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <Navbar />
      <div className="product-page-bg">
        {loading || !product ? <LoadingProduct /> : <ShowProduct />}
        <div className="container pt-3 pb-5" ref={reviewRef}>
          {product?.id && <ReviewBox productId={product.id} />}
        </div>
        {loadingSimilar ? <LoadingSimilar /> : <ShowSimilarProducts />}
      </div>
      <Footer />

      {/* Stock Notify Modal */}
      <StockNotifyModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        product={notifyProduct}
        notifyForm={notifyForm}
        setNotifyForm={setNotifyForm}
        navigate={navigate}
      />

      {/* Bulk & Wholesale WhatsApp Modal */}
      <BulkInquiryModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        product={product}
        variant={variants.find((v) => Number(v.id) === Number(id)) || null}
      />

      {showDescModal && (
        <div className="desc-modal">
          <div className="desc-modal-content p-4 rounded shadow">
            <button className="modal-close-btn" onClick={() => setShowDescModal(false)}>×</button>
            <h5 className="desc-modal-title">Product Description</h5>
            <div className="desc-text" >
              {product.product_description}
            </div>
            <div className="modal-footer justify-content-center">
              <button
                className="btn-green"
                onClick={() => setShowDescModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Product;